import React from 'react';
import classes from './DropZone.module.scss';
import { Accept, useDropzone } from 'react-dropzone';
import classNames from 'classnames';
import { WithChildren } from '../../../models/children-props';
import { Void } from '../../../models/basic-objects';
import { MaxLimits } from '../../../constants/media';

export type DropZoneViewProps = WithChildren & {
  acceptedTypes: Accept;
  maxFiles?: number;
  onDropRejectedHandler: Void;
  onDropAcceptedHandler: (files: File[]) => void;
  disabled?: boolean;
};

// @NOTE:
// Currently the fileValidator only looks at file size.
// However, this will mostly likely and should be extended to
// include video length and/or other considerations
export const fileValidator = (file: File) => {
  const maxLength = file.type.startsWith('image') ? 15728640 : 536870912; // 15MB - 15728640 bytes / 512MB - 536870912 bytes

  if (file.size > maxLength) {
    return {
      code: 'file-too-large',
      message: `File is larger than ${maxLength} bytes`
    };
  }

  return null;
};

export const DropZoneView = (props: DropZoneViewProps): JSX.Element => {
  const { maxFiles } = props;
  const { open, getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: maxFiles || MaxLimits.UPLOAD,
    noClick: true,
    noKeyboard: true,
    accept: props.acceptedTypes,
    onDropRejected: props.onDropRejectedHandler,
    onDropAccepted: (files: File[]) => props.onDropAcceptedHandler(files),
    validator: fileValidator,
    disabled: props.disabled
  });

  const dropZoneClasses = classNames(classes.DropZone, 'grid-x grid-padding-x margin-bottom-small-8', {
    [classes.DropZoneActive]: isDragActive
  });

  const dropZoneContainerClasses = classNames('cell', classes.DropZoneContainer);

  const dropZoneLabelWrapperClasses = classNames(classes.LabelWrapper, 'grid-x align-center');

  return (
    <>
      <div {...getRootProps()} className={dropZoneClasses}>
        <div className={dropZoneContainerClasses}>
          <input {...getInputProps()} />
          {isDragActive ? (
            <div className={dropZoneLabelWrapperClasses}>
              <h1>Drop files here to upload</h1>
            </div>
          ) : (
            <div>
              <div className={classes.DropZoneInstructions} onClick={open}>
                Drag and drop files here, or choose an option below
              </div>
              <div>{props.children}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
